import React, { useEffect, useMemo } from 'react';
import { getPagePathSpec } from 'common/components/CopyMagic/shared/dataHelpers';

import ConversationAssistantButton from '../ConversationAssistantButton';
import useOpenCloseListeners from '../../hooks/useOpenCloseListeners';
import useChatContext from '../../hooks/useChatContext';

type Props = {
  productArea: string;
  contextToken: string;
  floatingButton?: boolean;
  fullscreen?: boolean;
  prefix?: string;
  suffix?: string;
};

const DELAY = 3000; // 3 seconds delay to make sure that the user interacts with the page

const ConversationAssistantApp = ({
  productArea,
  contextToken: initialContextToken,
  floatingButton = true,
  fullscreen = false,
}: Props) => {
  const initialPagePath = useMemo(
    () =>
      getPagePathSpec(fullscreen && document.referrer ? document.referrer : window.location.href),
    [fullscreen],
  );

  const { chatContext, storeChatContext, restoreChatContext } = useChatContext({
    contextToken: initialContextToken,
    pagePath: initialPagePath,
  });

  // Initially store chat context if not in fullscreen mode
  useEffect(() => {
    if (document.visibilityState !== 'visible') return;
    if (fullscreen) return;

    storeChatContext();
  }, [fullscreen, storeChatContext]);

  useEffect(() => {
    const listener = () => {
      if (document.visibilityState !== 'visible') return;

      // Restore chat context when in fullscreen mode
      if (fullscreen) {
        restoreChatContext();
      } else {
        // Store chat context after a delay when not in fullscreen mode
        setTimeout(() => {
          if (document.visibilityState !== 'visible') return;
          storeChatContext();
        }, DELAY);
      }
    };

    window.addEventListener('visibilitychange', listener);
    return () => window.removeEventListener('visibilitychange', listener);
  }, [fullscreen, restoreChatContext, storeChatContext]);

  useOpenCloseListeners();

  return (
    <ConversationAssistantButton
      productArea={productArea}
      contextToken={chatContext.contextToken}
      floatingButton={floatingButton}
      fullscreen={fullscreen}
      pagePath={chatContext.pagePath}
    />
  );
};

export default ConversationAssistantApp;
